import { Temporal } from "./temporal.js";

//#region src/common/temporal-adapter.ts
/**
* DateAdapter provides an abstraction layer over the Temporal API
* to facilitate gradual migration from the Date API and provide
* common date/time operations with timezone awareness.
*
* This adapter is intentionally minimal at initial setup.
* Additional methods should be added as needed based on specific use cases.
*/
var DateAdapter = class DateAdapter {
	/**
	* Get the current instant in time
	*/
	static now() {
		return Temporal.Now.instant();
	}
	/**
	* Get the current date/time in a specific timezone
	* @param timeZone - IANA timezone identifier (e.g., 'America/New_York')
	*/
	static nowInTimeZone(timeZone) {
		return Temporal.Now.zonedDateTimeISO(timeZone);
	}
	/**
	* Parse an ISO 8601 string to an Instant
	* @param isoString - ISO 8601 formatted date-time string
	*/
	static fromISO(isoString) {
		return Temporal.Instant.from(isoString);
	}
	/**
	* Convert an Instant to an ISO 8601 string
	* @param instant - Temporal instant to convert
	*/
	static toISO(instant) {
		return instant.toString();
	}
	/**
	* Convert a Date object to a Temporal Instant
	* Useful for gradual migration from Date API
	* @param date - JavaScript Date object
	*/
	static fromDate(date) {
		return Temporal.Instant.fromEpochMilliseconds(date.getTime());
	}
	/**
	* Convert a Temporal Instant to a Date object
	* Useful for interoperability with libraries that expect Date objects
	* @param instant - Temporal instant to convert
	*/
	static toDate(instant) {
		return new Date(instant.epochMilliseconds);
	}
	/**
	* Get today's date as a PlainDate (calendar date without time/timezone)
	* This is timezone-independent and always represents the ISO calendar date.
	* Use this for date-only comparisons where time and timezone don't matter.
	*/
	static plainDateToday() {
		return Temporal.Now.plainDateISO();
	}
	/**
	* Create a PlainDate from year, month, and day components
	* @param year - Full year (e.g., 2024)
	* @param month - Month (1-12)
	* @param day - Day of month (1-31)
	* @throws {RangeError} If the date components are invalid
	*/
	static plainDateFrom(year, month, day) {
		return Temporal.PlainDate.from({
			year,
			month,
			day
		}, { overflow: "reject" });
	}
	/**
	* Parse a date string to a PlainDate
	* Supports both ISO 8601 date format (YYYY-MM-DD) and full ISO datetime strings
	* For datetime strings, extracts the date component regardless of timezone
	*
	* @param dateString - Date string to parse (e.g., '2024-03-15' or '2024-03-15T10:30:00-05:00')
	* @throws {RangeError} If the date string is invalid
	*
	* @example
	* // Plain date
	* DateAdapter.plainDateFromString('2024-03-15')
	*
	* @example
	* // Full datetime with timezone
	* DateAdapter.plainDateFromString('2024-03-15T10:30:00-05:00')
	*/
	static plainDateFromString(dateString) {
		if (dateString.includes("T") || dateString.includes(" ")) try {
			return Temporal.Instant.from(dateString).toZonedDateTimeISO("UTC").toPlainDate();
		} catch {
			try {
				return Temporal.PlainDateTime.from(dateString).toPlainDate();
			} catch {}
		}
		return Temporal.PlainDate.from(dateString);
	}
	/**
	* Compare two PlainDate objects
	* @param date1 - First date
	* @param date2 - Second date
	* @returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2
	*/
	static comparePlainDates(date1, date2) {
		return Temporal.PlainDate.compare(date1, date2);
	}
	/**
	* Subtract years from a PlainDate
	* @param date - Date to subtract from
	* @param years - Number of years to subtract
	*/
	static subtractYears(date, years) {
		return date.subtract({ years });
	}
	/**
	* Add years to a PlainDate
	* @param date - Date to add to
	* @param years - Number of years to add
	*/
	static addYears(date, years) {
		return date.add({ years });
	}
	/**
	* Check if today's date falls within a date range
	* Addresses DST issues by using PlainDate for calendar-date comparisons
	*
	* @param start - Start date (optional, ISO string or null)
	* @param end - End date (optional, ISO string or null)
	* @param options - Comparison options
	* @param options.inclusive - If true, use >= and <= comparisons; if false, use > and < (default: true)
	* @returns true if today is within the range, false otherwise
	*
	* @example
	* // Check if today is between Jan 1 and Dec 31, 2024 (inclusive)
	* DateAdapter.isInDateRange('2024-01-01', '2024-12-31')
	*
	* @example
	* // Check if today is after Jan 1, 2024 (no end date)
	* DateAdapter.isInDateRange('2024-01-01', null)
	*
	* @example
	* // Exclusive comparison (today must be strictly between dates)
	* DateAdapter.isInDateRange('2024-01-01', '2024-12-31', { inclusive: false })
	*/
	static isInDateRange(start, end, options = {}) {
		const { inclusive = true } = options;
		const today = DateAdapter.plainDateToday();
		try {
			if (start) {
				const startDate = DateAdapter.plainDateFromString(start);
				const comparison = DateAdapter.comparePlainDates(today, startDate);
				if (inclusive ? comparison < 0 : comparison <= 0) return false;
			}
			if (end) {
				const endDate = DateAdapter.plainDateFromString(end);
				const comparison = DateAdapter.comparePlainDates(today, endDate);
				if (inclusive ? comparison > 0 : comparison >= 0) return false;
			}
			return true;
		} catch {
			return false;
		}
	}
	/**
	* Check if today's date is strictly between two dates (exclusive comparison)
	* This is a convenience wrapper around isInDateRange with inclusive=false
	* Addresses DST issues by using PlainDate for calendar-date comparisons
	*
	* @param start - Start date (optional, ISO string or null)
	* @param end - End date (optional, ISO string or null)
	* @returns true if today is strictly between the dates (start < today < end), false otherwise
	*
	* @example
	* // Check if today is strictly between Jan 1 and Dec 31, 2024
	* DateAdapter.isBetweenDates('2024-01-01', '2024-12-31')
	*
	* @example
	* // Check if today is after Jan 1, 2024 (no end date)
	* DateAdapter.isBetweenDates('2024-01-01', null)
	*/
	static isBetweenDates(start, end) {
		return DateAdapter.isInDateRange(start, end, { inclusive: false });
	}
	/**
	* Validate if a date of birth meets minimum age requirement
	* Uses PlainDate for timezone-independent age calculations
	*
	* @param year - Birth year
	* @param month - Birth month (1-12)
	* @param day - Birth day (1-31)
	* @param minimumAge - Minimum age in years (default: 13)
	* @returns true if the person is at least minimumAge years old, false otherwise
	*
	* @example
	* // Check if someone born on March 15, 2010 is at least 13 years old
	* DateAdapter.validateAge(2010, 3, 15, 13)
	*/
	static validateAge(year, month, day, minimumAge = 13) {
		try {
			const dob = DateAdapter.plainDateFrom(year, month, day);
			const today = DateAdapter.plainDateToday();
			const minimumAgeDate = DateAdapter.subtractYears(today, minimumAge);
			return DateAdapter.comparePlainDates(dob, minimumAgeDate) <= 0;
		} catch {
			return false;
		}
	}
	/**
	* Check if a date is in the future
	* @param year - Year
	* @param month - Month (1-12)
	* @param day - Day (1-31)
	* @returns true if the date is after today, false otherwise
	*/
	static isFutureDate(year, month, day) {
		try {
			const date = DateAdapter.plainDateFrom(year, month, day);
			const today = DateAdapter.plainDateToday();
			return DateAdapter.comparePlainDates(date, today) > 0;
		} catch {
			return false;
		}
	}
	/**
	* Add days to a PlainDate
	* @param date - Date to add to
	* @param days - Number of days to add (can be negative)
	*/
	static addDays(date, days) {
		return date.add({ days });
	}
	/**
	* Subtract days from a PlainDate
	* @param date - Date to subtract from
	* @param days - Number of days to subtract
	*/
	static subtractDays(date, days) {
		return date.subtract({ days });
	}
	/**
	* Add months to a PlainDate
	* @param date - Date to add to
	* @param months - Number of months to add (can be negative)
	*/
	static addMonths(date, months) {
		return date.add({ months });
	}
	/**
	* Subtract months from a PlainDate
	* @param date - Date to subtract from
	* @param months - Number of months to subtract
	*/
	static subtractMonths(date, months) {
		return date.subtract({ months });
	}
	/**
	* Add weeks to a PlainDate
	* @param date - Date to add to
	* @param weeks - Number of weeks to add (can be negative)
	*/
	static addWeeks(date, weeks) {
		return date.add({ weeks });
	}
	/**
	* Subtract weeks from a PlainDate
	* @param date - Date to subtract from
	* @param weeks - Number of weeks to subtract
	*/
	static subtractWeeks(date, weeks) {
		return date.subtract({ weeks });
	}
	/**
	* Calculate the number of days between two dates
	* @param date1 - First date
	* @param date2 - Second date
	* @returns Number of days (positive if date2 is after date1, negative otherwise)
	*/
	static daysBetween(date1, date2) {
		return date1.until(date2, { largestUnit: "day" }).days;
	}
	/**
	* Calculate the number of months between two dates
	* @param date1 - First date
	* @param date2 - Second date
	* @returns Number of months (positive if date2 is after date1, negative otherwise)
	*/
	static monthsBetween(date1, date2) {
		return date1.until(date2, { largestUnit: "month" }).months;
	}
	/**
	* Calculate the number of years between two dates
	* @param date1 - First date
	* @param date2 - Second date
	* @returns Number of years (positive if date2 is after date1, negative otherwise)
	*/
	static yearsBetween(date1, date2) {
		return date1.until(date2, { largestUnit: "year" }).years;
	}
	/**
	* Calculate the elapsed days/hours/minutes between two date-times
	* @param start - Start date-time
	* @param end - End date-time
	* @returns Breakdown of elapsed time (negative values if end is before start)
	*
	* @example
	* const start = start.toPlainDateTime({ hour: 9, minute: 0 });
	* const end = end.toPlainDateTime({ hour: 11, minute: 0 });
	* DateAdapter.durationBetween(start, end)
	* // Returns: { days: 16, hours: 2, minutes: 0 }
	*/
	static durationBetween(start, end) {
		const diff = end.since(start, {
			largestUnit: "days",
			smallestUnit: "minutes"
		});
		return {
			days: diff.days,
			hours: diff.hours,
			minutes: diff.minutes
		};
	}
	/**
	* Calculate age from date of birth
	* @param dob - Date of birth
	* @returns Age in years
	*/
	static getAge(dob) {
		const today = DateAdapter.plainDateToday();
		return DateAdapter.yearsBetween(dob, today);
	}
	/**
	* Convert a date string or Date object to a PlainDate
	* @param dateInput - An ISO date string (e.g., '2000-01-15') or Date object
	* @throws {RangeError} If the date string is invalid
	*
	* @example
	* DateAdapter.plainDateFromInput('2000-01-15')
	* DateAdapter.plainDateFromInput(new Date('2000-01-15'))
	*/
	static plainDateFromInput(dateInput) {
		if (typeof dateInput === "string") return DateAdapter.plainDateFromString(dateInput);
		return DateAdapter.fromDate(dateInput).toZonedDateTimeISO("UTC").toPlainDate();
	}
	/**
	* Calculate age in years from a date of birth provided as a string or Date object
	* @param dateInput - Date of birth as an ISO date string (e.g., '2000-01-15') or Date object
	* @returns Age in years, or null if the input is invalid
	*
	* @example
	* DateAdapter.getAgeFromInput('2000-01-15') // Returns age in years
	* DateAdapter.getAgeFromInput(new Date('2000-01-15')) // Returns age in years
	*/
	static getAgeFromInput(dateInput) {
		try {
			const plainDate = DateAdapter.plainDateFromInput(dateInput);
			return DateAdapter.getAge(plainDate);
		} catch {
			return null;
		}
	}
	/**
	* Check if date components form a valid date without throwing an error
	* @param year - Year
	* @param month - Month (1-12)
	* @param day - Day (1-31)
	* @returns true if the date is valid, false otherwise
	*/
	static isValidDate(year, month, day) {
		try {
			Temporal.PlainDate.from({
				year,
				month,
				day
			}, { overflow: "reject" });
			return true;
		} catch {
			return false;
		}
	}
	/**
	* Check if a year is a leap year
	* @param year - Year to check
	* @returns true if the year is a leap year, false otherwise
	*/
	static isLeapYear(year) {
		try {
			return Temporal.PlainDate.from({
				year,
				month: 2,
				day: 1
			}).daysInMonth === 29;
		} catch {
			return false;
		}
	}
	/**
	* Get the number of days in a specific month
	* @param year - Year
	* @param month - Month (1-12)
	* @returns Number of days in the month
	*/
	static getDaysInMonth(year, month) {
		return Temporal.PlainDate.from({
			year,
			month,
			day: 1
		}).daysInMonth;
	}
	/**
	* Safely parse a date string without throwing an error
	* @param dateString - Date string to parse
	* @returns PlainDate if successful, null otherwise
	*/
	static tryParsePlainDate(dateString) {
		try {
			return DateAdapter.plainDateFromString(dateString);
		} catch {
			return null;
		}
	}
	/**
	* Check if date1 is before date2
	* @param date1 - First date
	* @param date2 - Second date
	* @returns true if date1 is before date2
	*/
	static isBefore(date1, date2) {
		return DateAdapter.comparePlainDates(date1, date2) === -1;
	}
	/**
	* Check if date1 is after date2
	* @param date1 - First date
	* @param date2 - Second date
	* @returns true if date1 is after date2
	*/
	static isAfter(date1, date2) {
		return DateAdapter.comparePlainDates(date1, date2) === 1;
	}
	/**
	* Check if two dates are the same calendar day
	* @param date1 - First date
	* @param date2 - Second date
	* @returns true if dates represent the same day
	*/
	static isSameDay(date1, date2) {
		return DateAdapter.comparePlainDates(date1, date2) === 0;
	}
	/**
	* Check if two dates are in the same month
	* @param date1 - First date
	* @param date2 - Second date
	* @returns true if dates are in the same month and year
	*/
	static isSameMonth(date1, date2) {
		return date1.year === date2.year && date1.month === date2.month;
	}
	/**
	* Check if two dates are in the same year
	* @param date1 - First date
	* @param date2 - Second date
	* @returns true if dates are in the same year
	*/
	static isSameYear(date1, date2) {
		return date1.year === date2.year;
	}
	/**
	* Get the earliest date from an array of dates
	* @param dates - Array of PlainDate objects
	* @returns The earliest date
	* @throws {Error} If the array is empty
	*/
	static min(...dates) {
		if (dates.length === 0) throw new Error("Cannot find min of empty array");
		return dates.reduce((earliest, current) => DateAdapter.isBefore(current, earliest) ? current : earliest);
	}
	/**
	* Get the latest date from an array of dates
	* @param dates - Array of PlainDate objects
	* @returns The latest date
	* @throws {Error} If the array is empty
	*/
	static max(...dates) {
		if (dates.length === 0) throw new Error("Cannot find max of empty array");
		return dates.reduce((latest, current) => DateAdapter.isAfter(current, latest) ? current : latest);
	}
	/**
	* Get the first day of the month for a given date
	* @param date - Date to get start of month for
	* @returns PlainDate representing the first day of the month
	*/
	static getStartOfMonth(date) {
		return date.with({ day: 1 });
	}
	/**
	* Get the last day of the month for a given date
	* @param date - Date to get end of month for
	* @returns PlainDate representing the last day of the month
	*/
	static getEndOfMonth(date) {
		return date.with({ day: date.daysInMonth });
	}
	/**
	* Get the first day of the year for a given date
	* @param date - Date to get start of year for
	* @returns PlainDate representing January 1st of the year
	*/
	static getStartOfYear(date) {
		return date.with({
			month: 1,
			day: 1
		});
	}
	/**
	* Get the last day of the year for a given date
	* @param date - Date to get end of year for
	* @returns PlainDate representing December 31st of the year
	*/
	static getEndOfYear(date) {
		return date.with({
			month: 12,
			day: 31
		});
	}
	/**
	* Check if a date is today
	* @param date - Date to check
	* @returns true if the date is today
	*/
	static isToday(date) {
		const today = DateAdapter.plainDateToday();
		return DateAdapter.isSameDay(date, today);
	}
	/**
	* Check if a date is in the past
	* @param date - Date to check
	* @returns true if the date is before today
	*/
	static isPast(date) {
		const today = DateAdapter.plainDateToday();
		return DateAdapter.isBefore(date, today);
	}
	/**
	* Get a date N days from now
	* @param days - Number of days from now (positive for future, negative for past)
	* @returns PlainDate N days from now
	*/
	static daysFromNow(days) {
		const today = DateAdapter.plainDateToday();
		return DateAdapter.addDays(today, days);
	}
	/**
	* Get a date N days ago
	* @param days - Number of days ago (always positive)
	* @returns PlainDate N days ago
	*/
	static daysAgo(days) {
		const today = DateAdapter.plainDateToday();
		return DateAdapter.subtractDays(today, days);
	}
	/**
	* Format a PlainDate using Intl.DateTimeFormat
	* @param date - Date to format
	* @param locale - Locale for formatting (e.g., 'en-US', 'en-GB')
	* @param options - Intl.DateTimeFormat options
	* @returns Formatted date string
	*
	* @example
	* const date = DateAdapter.plainDateFrom(2024, 3, 15);
	* DateAdapter.formatPlainDate(date, 'en-US', { dateStyle: 'full' })
	* // Returns: "Friday, March 15, 2024"
	*/
	static formatPlainDate(date, locale = "en-US", options = {}) {
		const isoString = date.toString();
		const jsDate = /* @__PURE__ */ new Date(`${isoString}T00:00:00`);
		return new Intl.DateTimeFormat(locale, options).format(jsDate);
	}
	/**
	* Formats a date to localized string using Intl.DateTimeFormat
	* Supports Date objects, milliseconds, or Temporal Instant
	*
	* @param date - Date to be converted. Can be a Date object, milliseconds, or Temporal Instant
	* @param options - Optional. Date Time Format options
	* @param locale - Optional. Locale string (e.g., 'en-US', 'es-ES')
	* @returns Formatted date string. Returns empty string if date is invalid.
	* @example
	* const instant = DateAdapter.now();
	* DateAdapter.formatToLocalizedDate(instant, { dateStyle: 'full' }, 'en-US')
	* // Returns: "Friday, March 15, 2024"
	*/
	static formatToLocalizedDate(date, options, locale) {
		try {
			if (typeof date === "object" && date !== null && "epochMilliseconds" in date) date = DateAdapter.toDate(date);
			return new Intl.DateTimeFormat(locale ?? "en-US", { ...options }).format(date);
		} catch (error) {
			console.error("Error formatting date:", error);
			return "";
		}
	}
	/**
	* Parse a date string from common formats (MM/DD/YYYY, DD-MM-YYYY, etc.)
	* @param dateString - Date string to parse
	* @param format - Format hint ('US' for MM/DD/YYYY, 'EU' for DD/MM/YYYY, 'ISO' for YYYY-MM-DD)
	* @returns PlainDate if successful
	* @throws {Error} If the date string cannot be parsed
	*
	* @example
	* DateAdapter.parseDateFromFormat('03/15/2024', 'US')
	* DateAdapter.parseDateFromFormat('15-03-2024', 'EU')
	*/
	static parseDateFromFormat(dateString, format = "ISO") {
		const trimmed = dateString.trim();
		if (format === "ISO" || /^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return Temporal.PlainDate.from(trimmed);
		const slashMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
		if (slashMatch) {
			const [, part1, part2, year] = slashMatch;
			const yearNum = Number.parseInt(year, 10);
			if (format === "US") {
				const month = Number.parseInt(part1, 10);
				const day = Number.parseInt(part2, 10);
				return Temporal.PlainDate.from({
					year: yearNum,
					month,
					day
				});
			} else if (format === "EU") {
				const day = Number.parseInt(part1, 10);
				const month = Number.parseInt(part2, 10);
				return Temporal.PlainDate.from({
					year: yearNum,
					month,
					day
				});
			}
		}
		throw new Error(`Unable to parse date string: ${dateString} with format: ${format}`);
	}
	/**
	* Convert a PlainDate to a ZonedDateTime in a specific timezone
	* @param plainDate - The plain date to convert
	* @param timeZone - IANA timezone identifier (e.g., 'America/New_York')
	* @param time - Optional time specification (defaults to midnight)
	* @returns ZonedDateTime in the specified timezone
	*
	* @example
	* const date = DateAdapter.plainDateFrom(2024, 3, 15);
	* const zoned = DateAdapter.toZonedDateTime(date, 'America/New_York', { hour: 10, minute: 30 })
	*/
	static toZonedDateTime(plainDate, timeZone, time = {}) {
		const { hour = 0, minute = 0, second = 0, millisecond = 0 } = time;
		return plainDate.toPlainDateTime({
			hour,
			minute,
			second,
			millisecond
		}).toZonedDateTime(timeZone);
	}
	/**
	* Check whether a given local (wall-clock) date/time actually occurs in
	* a timezone. Returns false for times that fall in a DST "spring
	* forward" gap (e.g. 2:30 AM on the day clocks skip from 2:00 to 3:00),
	* which have zero corresponding instants.
	*
	* Deliberately does NOT flag "fall back" ambiguous times (times that
	* occur twice) as nonexistent — those are valid, just ambiguous about
	* which offset applies, which isn't a validation error.
	*
	* @param plainDateTime - The local date/time to check
	* @param timeZone - IANA timezone identifier (e.g., 'America/New_York')
	*
	* @example
	* // 2:30 AM on a US spring-forward date doesn't exist
	* const dt = DateAdapter.plainDateFrom(2026, 3, 8).toPlainDateTime({ hour: 2, minute: 30 });
	* DateAdapter.wallTimeExistsInTimeZone(dt, 'America/New_York') // false
	*/
	static wallTimeExistsInTimeZone(plainDateTime, timeZone) {
		return plainDateTime.toZonedDateTime(timeZone, { disambiguation: "compatible" }).toPlainDateTime().equals(plainDateTime);
	}
	/**
	* Get the PlainDate for an instant in a specific timezone
	* Useful for getting "what date is it in New York right now?"
	*
	* @param instant - Temporal instant
	* @param timeZone - IANA timezone identifier (e.g., 'America/New_York')
	* @returns PlainDate in the specified timezone
	*
	* @example
	* const now = DateAdapter.now();
	* const dateInNY = DateAdapter.getDateInTimeZone(now, 'America/New_York')
	*/
	static getDateInTimeZone(instant, timeZone) {
		return instant.toZonedDateTimeISO(timeZone).toPlainDate();
	}
	/**
	* Check if native Temporal API is available
	* Useful for progressive enhancement, debugging, or telemetry
	*
	* @returns true if native Temporal is available, false if using polyfill
	*
	* @example
	* if (DateAdapter.hasNativeTemporalSupport()) {
	*     console.log('Using native Temporal API');
	* }
	*/
	static hasNativeTemporalSupport() {
		return typeof globalThis.Temporal !== "undefined" && typeof globalThis.Temporal.PlainDate !== "undefined";
	}
	/**
	* Check if the package is using the Temporal polyfill
	*
	* @returns true if using polyfill, false if using native Temporal
	*
	* @example
	* // Log for debugging or telemetry
	* console.log('Temporal polyfill active:', DateAdapter.isUsingPolyfill());
	*/
	static isUsingPolyfill() {
		return !DateAdapter.hasNativeTemporalSupport();
	}
};

//#endregion
export { DateAdapter };